import { getToolOrDynamicToolName, isToolUIPart } from 'ai'
import type { DynamicToolUIPart, ToolUIPart, UIMessage } from 'ai'
import {
  isUserAnsweredTool,
} from '@llj/ai-service-interface/dist/entities/tool-catalogue.interface'

// 提問工具的純邏輯（#1640、#1653）。哪支工具算提問，一律讀工具目錄的宣告，不寫死工具名。

/** 自行填寫列的提示字。模型被要求不要自己放「其他」選項，所以只會出現一次。 */
export const OTHER_OPTION_LABEL = '其他（自行填寫）'

/**
 * 伺服器「有待回答的提問」的錯誤碼。🔴 與 ai-service 的 AI_CONVERSATION_PENDING_QUESTION
 * 同一個值，由測試釘住；它與「對話已在別處更新」分開，收到時不重新載入。
 */
export const PENDING_QUESTION_CODE = 'AI060002'

/** 略過時伺服器說「那一份已經回答了」。收到就重讀使用者視圖，畫出當時的答案。 */
export const ALREADY_ANSWERED_CODE = 'AI060003'

export interface AskOption {
  label: string
  description: string
}

export interface AskQuestion {
  header: string
  question: string
  multiSelect: boolean
  // 沒有 ＝ 自由填寫題
  options: AskOption[] | null
}

export interface AskInput {
  preamble: string
  questions: AskQuestion[]
}

export type QuestionStatus = 'drafting' | 'pending' | 'answered' | 'unanswered'

type MessagePart = UIMessage['parts'][number]
type AnyToolPart = ToolUIPart | DynamicToolUIPart

const asString = (value: unknown): string => (typeof value === 'string' ? value : '')

const readOption = (value: unknown): AskOption | null => {
  const raw = value as { label?: unknown; description?: unknown } | null
  const label = asString(raw?.label)
  return label ? { label, description: asString(raw?.description) } : null
}

const readQuestion = (value: unknown): AskQuestion | null => {
  const raw = value as Record<string, unknown> | null
  if (!raw || typeof raw !== 'object') return null
  const options = Array.isArray(raw.options) ?
    raw.options.map(readOption).filter((o): o is AskOption => o !== null) : null
  return {
    header: asString(raw.header),
    question: asString(raw.question),
    multiSelect: raw.multiSelect === true,
    // 空陣列當自由填寫題：畫出一個沒有選項的選擇題，使用者什麼都選不了
    options: options && options.length > 0 ? options : null,
  }
}

/** 工具參數 → 提問內容。串流中參數可能只到一半，缺的欄位就是空的，不讓畫面炸掉。 */
export const readAskInput = (input: unknown): AskInput => {
  const raw = input as { preamble?: unknown; questions?: unknown } | null
  const questions = Array.isArray(raw?.questions) ? raw.questions : []
  return {
    preamble: asString(raw?.preamble),
    questions: questions.map(readQuestion).filter((q): q is AskQuestion => q !== null),
  }
}

/** 這個 part 是不是提問工具。 */
export const isQuestionPart = (part: MessagePart): boolean =>
  isToolUIPart(part) && isUserAnsweredTool(getToolOrDynamicToolName(part))

/**
 * 要不要當成一組提問來畫（#1642）。同一步多出來、被記成失敗的那一份，與參數不合法的
 * 那一份都不是：走一般工具摺疊列。有結果時只有 answered／unanswered 兩種才是。
 */
export const isQuestionSetPart = (part: MessagePart): boolean => {
  if (!isQuestionPart(part) || !isToolUIPart(part)) return false
  if (part.state === 'input-streaming' || part.state === 'input-available') return true
  if (part.state !== 'output-available') return false
  const status = (part.output as { status?: unknown } | null)?.status
  return status === 'answered' || status === 'unanswered'
}

/**
 * 這一組提問的狀態。還在產生參數時是 drafting；參數到齊、沒有結果就是等你回答；
 * 有結果時看結果裡的 status——只有明寫 answered 才算回答，其餘一律當未回答。
 */
export const questionStatus = (part: MessagePart): QuestionStatus => {
  if (!isToolUIPart(part)) return 'unanswered'
  if (part.state === 'input-streaming') return 'drafting'
  if (part.state === 'input-available') return 'pending'
  if (part.state === 'output-available') {
    const status = (part.output as { status?: unknown } | null)?.status
    return status === 'answered' ? 'answered' : 'unanswered'
  }
  return 'unanswered'
}

/** 答案摘要卡的一題：題目全文與答案。形狀由伺服器寫入（#1652），讀不到的欄位當沒有。 */
export interface AnsweredItem {
  question: string
  selected: string[]
  otherText: string
  skipped: boolean
}

export const readAnswers = (output: unknown): AnsweredItem[] => {
  const answers = (output as { answers?: unknown } | null)?.answers
  if (!Array.isArray(answers)) return []
  return answers.map((answer) => {
    const raw = answer as {
      question?: unknown; selected?: unknown; otherText?: unknown; skipped?: unknown
    } | null
    return {
      question: asString(raw?.question),
      selected: Array.isArray(raw?.selected) ? raw.selected.filter((s) => typeof s === 'string') : [],
      otherText: asString(raw?.otherText),
      skipped: raw?.skipped === true,
    }
  })
}

/** 摘要卡第二行：選項以「、」串接，自行填寫的字接在後面；略過就寫（略過）。 */
export const answerText = (item: AnsweredItem): string => {
  if (item.skipped) return '（略過）'
  return [...item.selected, item.otherText.trim()].filter(Boolean).join('、')
}

/**
 * 目前等待回答的那一份提問；沒有就是 null。
 * 只看最後一則助理訊息：不能開新的一輪，所以等待中的提問必在最後。
 */
export const findPendingQuestionPart = (messages: UIMessage[]): AnyToolPart | null => {
  const last = messages[messages.length - 1]
  if (!last || last.role !== 'assistant') return null
  const pending = last.parts.filter(
    (part) => isQuestionPart(part) && questionStatus(part) === 'pending',
  )
  const part = pending[pending.length - 1]
  return part && isToolUIPart(part) ? part : null
}

/** 每題的作答草稿。只存在前端：重新整理後等待回答的那一份從頭填。 */
export interface QuestionDraft {
  selected: string[]
  otherText: string
  skipped: boolean
}

export const EMPTY_DRAFT: QuestionDraft = { selected: [], otherText: '', skipped: false }

/** 翻頁器上每題的狀態：已答／略過／未處理 */
export type DraftState = 'answered' | 'skipped' | 'untouched'

export const DRAFT_STATE_TEXT: Record<DraftState, string> = {
  answered: '已答',
  skipped: '略過',
  untouched: '未處理',
}

/** 送給伺服器的一題答案（#1652）。略過的題目只送 skipped。 */
export interface SubmittedAnswer {
  selected?: string[]
  otherText?: string
  skipped?: true
}

/** 選了選項或自行填寫了非空白的字就算已答；作答一律優先於略過。 */
export const draftState = (draft: QuestionDraft): DraftState => {
  if (draft.selected.length > 0 || draft.otherText.trim()) return 'answered'
  return draft.skipped ? 'skipped' : 'untouched'
}

/** 點一個選項。單選與自行填寫二擇一：點了就清空打的字；複選切換勾選。 */
export const pickOption = (
  question: AskQuestion, draft: QuestionDraft, label: string,
): QuestionDraft => {
  if (!question.multiSelect) return { selected: [label], otherText: '', skipped: false }
  const has = draft.selected.includes(label)
  return {
    ...draft,
    selected: has ? draft.selected.filter((s) => s !== label) : [...draft.selected, label],
    skipped: false,
  }
}

/** 自行填寫列打字。單選題打出非空白字才取消已選的選項（空白不算作答）；複選可並存。 */
export const typeOther = (
  question: AskQuestion, draft: QuestionDraft, text: string,
): QuestionDraft => ({
  selected: !question.multiSelect && text.trim() ? [] : draft.selected,
  otherText: text,
  skipped: false,
})

/** 略過這一題：清掉已作的答，伺服器要求略過時不能帶選項與文字。 */
export const skipDraft = (): QuestionDraft => ({ selected: [], otherText: '', skipped: true })

/** 每一題都是已答或略過才能送出；全部略過也可以。 */
export const canSubmitDrafts = (questions: AskQuestion[], drafts: QuestionDraft[]): boolean =>
  questions.length > 0 &&
  questions.every((_, index) => draftState(drafts[index] ?? EMPTY_DRAFT) !== 'untouched')

/** 草稿 → 送出的答案。自由填寫題沒有選項，答案全在 otherText。 */
export const toSubmittedAnswer = (draft: QuestionDraft): SubmittedAnswer => {
  if (draftState(draft) === 'skipped') return { skipped: true }
  const otherText = draft.otherText.trim()
  return { selected: [...draft.selected], ...(otherText ? { otherText } : {}) }
}

/**
 * 送出後就地顯示的結果，形狀與伺服器寫進事件的一致，重新整理前後畫法相同。
 * 🔴 只給畫面用：伺服器不收它，結果由伺服器依答案重組。
 */
export const toAnsweredOutput = (input: AskInput, answers: SubmittedAnswer[]) => ({
  status: 'answered' as const,
  answers: input.questions.map((question, index) => {
    const { selected = [], otherText, skipped } = answers[index] ?? {}
    return {
      header: question.header,
      question: question.question,
      selected,
      ...(otherText ? { otherText } : {}),
      ...(skipped ? { skipped } : {}),
    }
  }),
})

/** 等著送出的答案；sendAutomaticallyWhen 與續跑請求都讀它 */
export interface PendingAnswer {
  toolCallId: string
  answers: SubmittedAnswer[]
}

/**
 * 要不要自動送出續跑請求：只在剛填入答案的那一份確實已回答時。
 * 🔴 認 pending 那一份而不是掃訊息：續跑串完後同一則訊息裡仍有這組已回答的提問，
 * 只看訊息會再觸發一次。
 */
export const shouldSendAnswer = (
  messages: UIMessage[], pending: PendingAnswer | null,
): boolean => {
  if (!pending) return false
  const last = messages[messages.length - 1]
  if (!last || last.role !== 'assistant') return false
  return last.parts.some((part) => isToolUIPart(part) && isQuestionPart(part) &&
    part.toolCallId === pending.toolCallId && questionStatus(part) === 'answered')
}

/** 續跑請求的 body：只送 toolCallId 與答案，不送整則訊息（伺服器也不讀）。 */
export const answerRequestBody = (conversationId: string | null, pending: PendingAnswer) => ({
  conversationId,
  toolCallId: pending.toolCallId,
  answers: pending.answers,
})

/** 這一次 /ai/chat 請求是不是回答續跑。transport 的錯誤處理據此分流。 */
export const isAnswerRequest = (init?: RequestInit): boolean => {
  if (typeof init?.body !== 'string') return false
  try {
    return typeof (JSON.parse(init.body) as { toolCallId?: unknown }).toolCallId === 'string'
  } catch {
    return false
  }
}

/** 略過成功後就地把那一份標成未回答，不必整份重讀。其他訊息原封不動。 */
export const markQuestionUnanswered = (
  messages: UIMessage[], toolCallId: string, output: unknown,
): UIMessage[] => messages.map((message) => {
  const hit = message.parts.some((part) => isToolUIPart(part) && part.toolCallId === toolCallId)
  if (!hit) return message
  return {
    ...message,
    parts: message.parts.map((part) => (
      isToolUIPart(part) && part.toolCallId === toolCallId ?
        { ...part, state: 'output-available', output, errorText: undefined } as MessagePart :
        part
    )),
  }
})
